import _get from 'lodash/get';
import {
  AiIndexingDocumentationsQueryVariables,
  DocumentationFilterInputObject,
  useAiIndexingDocumentationsQuery,
} from 'graphql-common';
import { GetUrlParams } from '@lib/enums/urls';
import getIndexingDocumentsSorts from 'routes/CompanyPlan/utils/getIndexingDocumentsSorts';
import { IndexingDocumentationItem } from 'routes/CompanyPlan/utils/types';
import { ListQueryHookProps } from '../types';

interface Props extends ListQueryHookProps {
  // The whole filter set (parked / not-indexable / failed — see
  // getIndexingDocumentFilters), not merged onto a base set: unlike most list
  // hooks this one has no default filter of its own.
  filters: DocumentationFilterInputObject[];
}

/**
 * The documents behind one of the indexing card's non-searchable counts —
 * parked, not-indexable (SKIPPED), or failed — same query, different filter.
 *
 * **Only ever call this for a full admin on a company with the AI plan.** The
 * filter is gated, and a rejected filter nulls the whole response rather than
 * dropping a field — so for anyone else this query returns nothing at all,
 * which is why the caller gates the modal rather than the rendering.
 */
export default function useAiIndexingDocumentationsQueryHook({
  fetchPolicy = 'cache-and-network',
  filters,
  orderingParams,
  paginationParams,
  skip,
}: Props) {
  const page = _get(paginationParams, GetUrlParams.Page);
  const perPage = _get(paginationParams, GetUrlParams.PerPage);
  const queryVariables: AiIndexingDocumentationsQueryVariables = {
    page: Number(page),
    limit: Number(perPage),
    sorts: getIndexingDocumentsSorts(orderingParams || {}),
    filters,
  };
  const query = useAiIndexingDocumentationsQuery({
    fetchPolicy,
    variables: queryVariables,
    skip,
  });
  const { loading, data, previousData, refetch, error } = query;
  const queryData = data || previousData;
  const firstLoading = loading && previousData === undefined;
  const collection = _get(
    queryData,
    'data.collection',
    [],
  ) as IndexingDocumentationItem[];
  const metadata = _get(queryData, 'data.metadata');
  const totalCount = _get(metadata, 'totalCount', 0);
  const totalPages = _get(metadata, 'totalPages', 1);

  return {
    collection,
    error,
    firstLoading,
    loading,
    refetch,
    totalCount,
    totalPages,
  };
}
